import React, { useEffect } from 'react';
import _ from 'underscore';
import { titleize, humanize } from 'underscore.string';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';

import { stateCodes } from 'components/modules/user/user-form-utils';
import { useTranslation } from 'react-i18next';

import 'utils/selectize';
import { sluggify } from '../../../../utils/localization/utils';

const REQUIRED_ATTRIBUTES = [
  'first_name',
  'last_name',
  'username',
  'phone',
  'email',
  'company_name',
  'street',
  'city',
  'state',
  'zipcode',
];
const INITIAL_ERROR_OBJECT = {
  first_name: {},
  last_name: {},
  username: {},
  phone: {},
  email: {},
  company_name: {},
  address: {},
};
const ADDRESS_FIELDS = ['street', 'street2', 'city', 'state', 'zipcode'];
const CONSIGNEE_FIELDS = [
  'first_name',
  'last_name',
  'username',
  'phone',
  'email',
  'company_name',
];
const getRequiredAsterisk = (field) => {
  return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};

const ConsigneeForm = ({ details, category, onSave }) => {
  const [errors, setErrors] = React.useState(INITIAL_ERROR_OBJECT);
  const [changed, setChanged] = React.useState({});
  const [consignee, setConsignee] = React.useState(details);
  const { t } = useTranslation();

  useEffect(() => {
    updateErrorObj();
  }, []);
  function updateErrorObj() {
    if (category === 'address') {
      areRequiredFieldsPresent(consignee.address, ADDRESS_FIELDS);
    } else {
      areRequiredFieldsPresent(consignee, CONSIGNEE_FIELDS);
    }
  }
  const areRequiredFieldsPresent = (parent, fields) => {
    const errorsObj = errors;
    fields.forEach((field) => {
      errorsObj[field] =
        !parent[field] && REQUIRED_ATTRIBUTES.includes(field)
          ? { message: t('FIELD_REQUIRED_TIP', { field: titleize(t(sluggify(field))) }) }
          : {};
    });
    setErrors(errorsObj);
    const allPresent = _.every(fields, (f) => _.isEmpty(errors[f]));
    return allPresent;
  };

  const handleAddressFieldChange = (field, value) => {
    const address = consignee.address;
    address[field] = value;
    setConsignee((consignee) => ({
      ...consignee,
      address,
    }));
    setChanged({ address: true });
    const errorsObj = { ...errors };
    errorsObj[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: t('FIELD_REQUIRED_TIP', { field: titleize(t(sluggify(field))) }),
          }
        : {};
    setErrors(errorsObj);
  };

  const handleFieldChange = (field, value) => {
    setConsignee((consignee) => ({
      ...consignee,
      [field]: value,
    }));
    setChanged((changed) => ({
      ...changed,
      [field]: true,
    }));
    const errorsObj = { ...errors };
    errorsObj[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: t('FIELD_REQUIRED_TIP', { field: titleize(t(sluggify(field))) }),
          }
        : {};
    setErrors(errorsObj);
  };

  const saveDetails = () => {
    if (category === 'address') {
      if (!areRequiredFieldsPresent(consignee.address, ADDRESS_FIELDS)) {
        return;
      }
    } else if (!areRequiredFieldsPresent(consignee, CONSIGNEE_FIELDS)) {
      return;
    }

    _.map(changed, (item, key) => {
      if (key === 'address') {
        const address = consignee.address;
        _.map(consignee.address, (item, key) => {
          address[key] = address[key].trim();
        });
        consignee.address = address;
      }
    });
    const updates = _.pick(consignee, Object.keys(changed));
    updates._id = consignee._id;
    onSave(updates);
  };

  function errorsExist() {
    const errorsObj = errors;
    let errorsExist = false;
    _.map(errorsObj, (value, key) => {
      if (!_.isEmpty(errorsObj[key])) errorsExist = true;
    });
    return errorsExist;
  }

  return (
    <div>
      {category === 'account' && (
        <div className="grid sm:grid-cols-6 sm:gap-4 gap-y-6">
          <CargomaticInput
            className="sm:col-span-6"
            error={errors.company_name}
            label={`${t('COMPANY_NAME')} ${getRequiredAsterisk(
              'company_name',
            )}`}
            info={t('CONSIGNEE_COMPANY_NAME')}
            onChange={(value) => handleFieldChange('company_name', value)}
            value={consignee?.company_name}
          />
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.first_name}
            label={`${t('FIRST_NAME')}  ${getRequiredAsterisk('first_name')}`}
            info={t('CONSIGNEE_FIRST_NAME')}
            onChange={(value) => handleFieldChange('first_name', value)}
            value={consignee?.first_name}
          />
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.last_name}
            label={`${t('LAST_NAME')} ${getRequiredAsterisk('last_name')}`}
            info={t('CONSIGNEE_LAST_NAME')}
            onChange={(value) => handleFieldChange('last_name', value)}
            value={consignee?.last_name}
          />
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.phone}
            label={`${t('PHONE_NUMBER')} ${getRequiredAsterisk('phone')}`}
            info={t('CONSIGNEE_PHONE_NUMBER')}
            onChange={(value) => handleFieldChange('phone', value)}
            value={consignee?.phone}
          />
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.username}
            label={`${t('USERNAME')} ${getRequiredAsterisk('username')}`}
            info={t('CONSIGNEE_USERNAME')}
            onChange={(value) => handleFieldChange('username', value)}
            value={consignee?.username}
          />
          <CargomaticInput
            className="sm:col-span-4"
            error={errors.email}
            label={`${t('EMAIL_ADDRESS')} ${getRequiredAsterisk('email')}`}
            info={t('CONSIGNEE_EMAIL_ADDRESS')}
            onChange={(value) => handleFieldChange('email', value)}
            value={consignee?.email}
            maxLength={254}
          />
        </div>
      )}
      {category === 'address' && (
        <div>
          <h3 className="mt-0 mb-4">{t('ADDRESS_INFO')}</h3>
          <div className="grid sm:grid-cols-3 sm:gap-4 gap-y-6">
            <CargomaticInput
              className="sm:col-span-full"
              error={errors.street}
              label={`${titleize(t('STREET'))} ${getRequiredAsterisk(
                'street',
              )}`}
              info={t('CONSIGNEE_STREET')}
              onChange={(value) => {
                handleAddressFieldChange('street', value);
              }}
              value={consignee?.address?.street}
            />
            <CargomaticInput
              className="sm:col-span-full"
              error={errors.street2}
              label={`${titleize(t('STREET_2'))} ${getRequiredAsterisk(
                'street2',
              )}`}
              info={t('CONSIGNEE_STREET_2')}
              onChange={(value) => {
                handleAddressFieldChange('street2', value);
              }}
              value={consignee?.address?.street2}
            />
            <CargomaticInput
              error={errors.city}
              label={`${titleize(t('CITY'))} ${getRequiredAsterisk('city')}`}
              info={t('CONSIGNEE_CITY')}
              onChange={(value) => {
                handleAddressFieldChange('city', value);
              }}
              value={consignee?.address?.city}
            />
            <CargomaticSelect
              error={errors.state}
              selected={consignee?.address?.state}
              info={t('CONSIGNEE_STATE')}
              items={stateCodes}
              label={`${titleize(t('STATE'))} ${getRequiredAsterisk('state')}`}
              valueKey="abbreviation"
              textKey="name"
              onSelect={(item) => {
                handleAddressFieldChange('state', item.abbreviation);
              }}
            />
            <CargomaticInput
              error={errors.zipcode}
              label={`${t('ZIP_CODE')} ${getRequiredAsterisk('zipcode')}`}
              info={t('CONSIGNEE_POSTAL_CODE')}
              onChange={(value) => {
                handleAddressFieldChange('zipcode', value);
              }}
              value={consignee?.address?.zipcode}
            />
          </div>
        </div>
      )}
      <div className="my-2 form-actions">
        <button
          className="button button-green"
          onClick={saveDetails}
          disabled={errorsExist()}
        >
          {t('SAVE')}
        </button>
      </div>
    </div>
  );
};
export default ConsigneeForm;
